import {
  getApiDebuggingId,
  getApiHttpStatus,
  getErrorKind,
  getSurfaceableApiDetail,
} from 'helpers/rtk-utils';
import { PositionCategoryOptions } from 'views/Admins/constants';
import { RecipientAdminRow } from 'services/recipientAdminsApi';
import { RecipientAdminOperation } from './types';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export const isTermsAccepted = (row: RecipientAdminRow): boolean => row.tos_accepted != null;

// Mirrors the API, which computes archived_count as `status != ACTIVE` rather than `== CLOSED`.
export const isArchivedRow = (row: RecipientAdminRow): boolean => row.status !== 'ACTIVE';

export const getStatusLabel = (row: RecipientAdminRow): string =>
  isArchivedRow(row) ? 'Archived' : 'Active';

// Falls back to the raw value: the super-admin form stores free text for "Other".
export const getPositionCategoryLabel = (positionCategory: string): string =>
  PositionCategoryOptions.find(option => option.value === positionCategory)?.label ??
  positionCategory;

export const canArchiveRow = (row: RecipientAdminRow): boolean =>
  !isArchivedRow(row) && !row.is_primary_contact;

export const canRestoreRow = (
  row: RecipientAdminRow,
  activeCount: number,
  activeAdminLimit: number
): boolean => isArchivedRow(row) && activeCount < activeAdminLimit;

// Client-side check against the roster already on screen - never a new lookup call, so it
// can't be used to probe whether an arbitrary email belongs to an existing Oath admin.
export const isDuplicateEmail = (email: string, admins: RecipientAdminRow[]): boolean => {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return false;
  return admins.some(admin => admin.email.toLowerCase() === normalized);
};

// The admin API's detailed handlers put the raised message straight into `detail`, so surface it
// rather than re-author the copy here. Only a 404 (grant gone) and a 409 (already active, already
// archived, primary contact, at the cap) reach these routes carrying copy written for a campaign;
// every other status is internal text - a 403 names the denied activity, a 422's `message` is a
// fixed "Request Validation failed.", a 5xx is "Internal Server Error" - and takes the fallback.
const SURFACEABLE_ERROR_STATUSES = [404, 409];

// The shared http_exception_handler wraps every HTTPException in the same {detail, debugging_id}
// envelope, Starlette's unmatched-route 404 included, so the 404 lane also admits this framework
// sentinel. Excluded so a misrouted URL falls through to the fallback instead of putting a bare
// "Not Found" on a campaign's toast.
const FRAMEWORK_NOT_FOUND_DETAIL = 'Not Found';

const getGuardrailDetail = (error: unknown): string | undefined => {
  const detail = getSurfaceableApiDetail(error, SURFACEABLE_ERROR_STATUSES);
  return detail?.trim() === FRAMEWORK_NOT_FOUND_DETAIL ? undefined : detail;
};

export const getRecipientAdminErrorMessage = (
  error: unknown,
  fallback: string = DEFAULT_ERROR_MESSAGE
): string => getGuardrailDetail(error) ?? fallback;

export type RecipientAdminFailure = {
  message: string;
  severity: 'info' | 'error';
  logMessage: string;
  logContext: Record<string, unknown>;
};

// The refusals this feature surfaces - the active-admin cap, an already-archived grant, the
// primary contact - are guardrails the product expects, and the backend logs them at INFO too.
// Keep the severity honest so an admin error monitor doesn't fire on a working guardrail, and the
// two lanes' log messages distinct so a monitor keying on message text can tell them apart.
export const describeRecipientAdminFailure = (
  error: unknown,
  operation: RecipientAdminOperation,
  fallback: string = DEFAULT_ERROR_MESSAGE
): RecipientAdminFailure => {
  const message = getRecipientAdminErrorMessage(error, fallback);
  const guardrailDetail = getGuardrailDetail(error);
  const logContext: Record<string, unknown> = {
    httpStatus: getApiHttpStatus(error),
    // snake_case deliberately: matches the backend's correlation_id facet so one query joins a
    // browser log to its backend log. Don't camelCase it.
    correlation_id: getApiDebuggingId(error),
  };

  if (guardrailDetail !== undefined) {
    return {
      message,
      severity: 'info',
      logMessage: `Recipient admin ${operation} rejected by a guardrail`,
      // Safe only while these details stay server constants - one naming a person would leak them.
      logContext: { ...logContext, guardrailDetail },
    };
  }

  // Nothing here may derive from the response body: the add route's 422 echoes the submitted name,
  // email and phone. errorKind is a transport status or an Error.name, never the body.
  return {
    message,
    severity: 'error',
    logMessage: `Failed to ${operation} recipient admin`,
    logContext: { ...logContext, errorKind: getErrorKind(error) },
  };
};
